import { Avatar } from '@creditornot/cb-avatar';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { Popover } from '@creditornot/cb-popover';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { Typography } from 'common/typography';
import { useMe } from 'modules/experience/hooks/useMe';
import { getInitials } from 'modules/experience/utils';

import { UserDropDown } from '../UserDropDown/UserDropDown';

const UserContainer = styled.div<{ isSidebarOpen: boolean }>`
  display: flex;
  cursor: pointer;
  align-self: stretch;
  height: 68px;
  gap: 8px;
  background-color: ${({ isSidebarOpen }) => (isSidebarOpen ? cssThemedValue('surfaceMain') : 'transparent')};
  margin: 16px;
  padding: ${({ isSidebarOpen }) => (isSidebarOpen ? '16px' : 'initial')};
  align-items: center;
  border-radius: 12px;
  justify-content: ${({ isSidebarOpen }) => (isSidebarOpen ? 'flex-start' : 'center')};
`;

const UserDescription = styled.div<{ isSidebarOpen: boolean }>`
  display: ${({ isSidebarOpen }) => (isSidebarOpen ? 'flex' : 'none')};
  flex-direction: column;
  gap: 2px;
  align-self: stretch;
  height: 44px;
  overflow: hidden;
`;

const UserEmail = styled(Typography.Small)`
  color: ${({ theme }) => theme.cbSemanticColors.textSecondary};
`;

const StyledAvatar = styled(Avatar)`
  width: 24px;
  height: 24px;
`;

export const User = ({ isSidebarOpen }: { isSidebarOpen: boolean }) => {
  const { data: user } = useMe();

  const profilePicUrl = useMemo(() => {
    let profilePicUrl;
    if (user && user.profile_photo) {
      profilePicUrl = user.profile_photo.url;
    } else if (user && user.profile_picture) {
      profilePicUrl = user.profile_picture.url;
    }
    return profilePicUrl;
  }, [user]);

  const email = useMemo(() => user && user.email, [user]);
  const initials = useMemo(() => user && getInitials(user.name?.first_name ?? ''), [user]);

  const [show, setShow] = useState(false);

  return (
    <Popover
      content={<UserDropDown />}
      onClickOutside={() => setShow(false)}
      show={show}
      renderArrow={false}
      disableContentPadding
    >
      <UserContainer isSidebarOpen={isSidebarOpen} onClick={() => setShow(!show)}>
        <StyledAvatar size="small" imgSrc={profilePicUrl} initials={initials} />
        <UserDescription isSidebarOpen={isSidebarOpen}>
          <Typography.Body3>
            {user?.name?.first_name} {user?.name?.last_name}
          </Typography.Body3>
          <UserEmail>{email}</UserEmail>
        </UserDescription>
      </UserContainer>
    </Popover>
  );
};
